<template>
    <div class="container mt-4">
        <div class="row extra-center extra-center">
            <transition-group class="row" name="flip-list" tag="div">
                <div
                    :key="person.name"
                    class="col-4 col-lg-4"
                    v-for="person in people"
                >
                    <div class="card profile-card">
                        <div class="background-block">
                            <img :alt="`${person.name} pozadina`" :src="person.background" class="background" />
                        </div>
                        <div class="profile-thumb-block">
                            <img :alt="`${person.name} profilna slika`" :src="person.image" class="profile" />
                        </div>
                        <div class="card-content">
                            <h2>{{ person.name }}</h2>
                            <draggable
                                :disabled="!gameRunning"
                                :key="person.assigned"
                                :list="[ descriptions[person.assigned] ]"
                                :move="hideHoveringElement"
                                :zIndex="9999"
                                :data-key="person.name"
                                :data-is-being-dragged="currentlyDraggingName === person.name"

                                @start="onDragStart(person.name, $event)"
                                @touchmove.native="onDragging"
                                @change="personChange(person.name, $event)"
                                @end="onDragEnd"

                                class="list-group drop-here card-text"
                                group="people"
                                ref="dragContainer"
                            >
                                <div
                                    :class="{
                                        'draggable': gameRunning,
                                        'correct': gameOver && person.assigned === person.correct,
                                        'wrong': gameOver && person.assigned !== person.correct,
                                        'd-none': person.name === currentlyHoveringName,
                                    }"
                                    class="list-group-item"
                                >
                                    {{ descriptions[person.assigned] }}
                                </div>
                            </draggable>
                        </div>
                    </div>
                </div>
            </transition-group>

            <div class="row mt-3">
                <div class="col buttons">
                    <transition name="slide-fade">
                        <b-alert
                            :show="gameOver"
                        >
                            <strong>Točno: {{ correctAnswerCount }}/{{ people.length }}</strong>
                        </b-alert>
                    </transition>

                    <transition name="slide-fade">
                        <b-alert
                            :show="gameRestarting"
                            type="info"
                        >
                            <strong>Miješam...</strong>
                        </b-alert>
                    </transition>


                    <div class="text-center">
                        <transition name="slide-fade" mode="out-in">
                            <button
                                :key="'running'"
                                @click="checkAnswers"
                                class="btn btn-info"
                                v-if="gameRunning"
                            >
                                Provjeri
                            </button>
                            <button
                                :key="'over'"
                                @click="restartGame"
                                class="btn btn-primary"
                                type="button"
                                v-if="gameOver"
                            >
                                Igraj ponovno
                            </button>
                        </transition>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import draggable from 'vuedraggable';

    import ZelenkoBg from '@/assets/img/bgZ.png';
    import ZelenkoProfile from '@/assets/img/superZ.png';

    import MisicavkoBg from '@/assets/img/bgM.png';
    import MisicavkoProfile from '@/assets/img/superM.png';

    import StrahomirBg from '@/assets/img/bgS.png';
    import StrahomirProfile from '@/assets/img/superS.png';

    /**
     * Shuffles array in place. ES6 version
     * @param {Array} a items An array containing the items.
     */
    const shuffle = (a) => {
        for (let i = a.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [ a[ i ], a[ j ] ] = [ a[ j ], a[ i ] ];
        }

        return a;
    };

    const GAMESTATE = {
        running: 'running',
        over: 'over',
        restarting: 'restarting',
    };

    export default {
        name: 'custom-clone',

        display: 'Custom Clone',

        order: 3,

        components: {
            draggable,
        },

        data() {
            return {
                descriptions: [
                    'Ja sam ljubitelj tjelovježbe i sporta. Stvoren sam za duge staze i teške napore. Najsnažniji sam čovjek svih vremena.',
                    'Ja sam junak sa zelenim plaštem. Moji su neprijatelji grickalice i nezdrava hrana. Volim jesti sve što je zdravo.',
                    'Ja sam neustrašiva djevojčica. Hrabrija sam od svih svojih neprijatelja. Borim se protiv svih vaših strahova.',
                ],

                people: [
                    {
                        name: 'Mišićavko',
                        correct: 0,
                        image: MisicavkoProfile,
                        background: MisicavkoBg,
                    },
                    {
                        name: 'Zelenko',
                        correct: 1,
                        image: ZelenkoProfile,
                        background: ZelenkoBg,
                    },
                    {
                        name: 'Strahomira',
                        correct: 2,
                        image: StrahomirProfile,
                        background: StrahomirBg,
                    },
                ],

                gameState: GAMESTATE.running,

                currentlyHoveringName: '',
                currentlyDraggingName: '',

                coords: {
                    x: 0,
                    y: 0,
                },
            };
        },

        created() {
            this.doRestart();
        },

        computed: {
            correctAnswerCount() {
                return this.people.filter(({ correct, assigned }) => assigned === correct).length;
            },

            descriptionsMap() {
                return Object.fromEntries(
                    this
                        .descriptions
                        .map(
                            (v, k) =>
                                [ v, k ]
                            ,
                        )
                    ,
                );
            },

            gameRunning() {
                return this.gameState === GAMESTATE.running;
            },

            gameOver() {
                return this.gameState === GAMESTATE.over;
            },

            gameRestarting() {
                return this.gameState === GAMESTATE.restarting;
            },
        },

        methods: {
            personChange(personName, { added } = {}) {
                if (!added) {
                    return;
                }

                const { element } = added;
                const stringId = this.descriptionsMap[ element ];

                const personRemovedFrom = this.people.find((person) => person.assigned === stringId);
                const personAddedTo = this.people.find((person) => person.name === personName);

                personRemovedFrom.assigned = personAddedTo.assigned;
                personAddedTo.assigned = stringId;
            },

            checkAnswers() {
                this.gameState = GAMESTATE.over;
            },

            restartGame() {
                this.doRestart();
                this.gameState = GAMESTATE.restarting;
                // Wait for animation to end
                setTimeout(() => this.gameState = GAMESTATE.running, 1000);
            },

            /**
             * Shuffle each person's assigned property in-place
             */
            shuffleAssigned() {
                const { people, descriptions } = this;

                const randomizedDescriptionIds = shuffle(
                    Array(descriptions.length)
                        .fill(0)
                        .map((_, i) => i),
                );

                for (const [ i, person ] of Object.entries(people)) {
                    this.$set(person, 'assigned', randomizedDescriptionIds[ i ]);
                }

                return people;
            },

            shufflePeople() {
                const { people } = this;

                return shuffle(people);
            },

            doRestart() {
                this.shuffleAssigned();
                this.shufflePeople();
            },

            hideHoveringElement({ from: fr, to }) {
                if (fr === to) {
                    this.currentlyHoveringName = '';
                } else {
                    this.currentlyHoveringName = to.dataset.key;
                }
            },

            onDragStart(personName, { originalEvent: { x, y } }) {
                this.coords.x = x;
                this.coords.y = y;
                this.currentlyDraggingName = personName;
            },

            onDragging({ targetTouches = [] }) {
                const [ touch ] = targetTouches;

                if (!touch) {
                    return;
                }

                // Get the Sortable ghost element
                const ghostEl = document.querySelector('.sortable-drag');

                if (!ghostEl) {
                    return;
                }

                const { clientX, clientY } = touch;
                const { x, y } = this.coords;

                const dx = clientX - x;
                const dy = clientY - y;

                // Ensure ghost element is the first child (required, not 100% sure why)
                document.body.prepend(ghostEl);
                // Override transform matrix with local values
                ghostEl.style.transform = `matrix(1, 0, 0, 1, ${ dx }, ${ dy })`;
            },

            onDragEnd() {
                this.currentlyHoveringName = '';
                this.currentlyDraggingName = '';
            },
        },
    };
</script>

<style scoped>
    .list-group[data-is-being-dragged] > * {
        opacity: 0;
    }

    .list-group-item {
        line-height: 1.2em;
        height: 100%;
        transition: background-color 1s ease-in-out;
        background-color: #FFFFFF;
        padding: .75rem 1rem;
    }

    .slide-fade-enter-active {
        transition: all .3s ease-in-out;
    }

    .slide-fade-leave-active {
        transition: all .8s cubic-bezier(1.0, 0.5, 0.8, 1.0);
    }

    .slide-fade-enter, .slide-fade-leave-to {
        transform: translateY(-10px);
        opacity: 0;
    }

    .list-group-item.draggable {
        cursor: grab;
    }

    .correct {
        background-color: #52D7E0 !important;
    }

    .wrong {
        background-color: #E9969F !important;
    }

    .col-4 {
        font-size: 100%;
        transition-property: padding, font-size;
        transition-duration: .5s;
        transition-timing-function: ease-in-out;
    }

    .profile-card h2 {
        font-size: 150%;
    }

    @media screen and (max-width: 992px) {
        .col-4 {
            font-size: 90%;
            padding: 0 2px;
        }

        .profile-card .card-content {
            padding-right: 5px;
            padding-left: 5px;
        }

        .profile-card h2 {
            font-size: 150%;
        }

        .profile-card .profile {
            max-width: 120px;
        }

        .list-group-item {
            overflow: hidden;
            padding: 2px;
            border: none;
            background: transparent;
        }
    }

    @media screen and (max-width: 760px) {
        .col-4 {
            font-size: 80%;
        }

        .list-group-item {
            font-size: 90%;
        }

        .profile-card h2 {
            font-size: 120%;
        }
    }

    .list-group-item.sortable-ghost + .list-group-item {
        display: none;
    }

    .flip-list-move {
        transition: transform 1s;
    }
</style>
